import React from 'react';
import * as CONSTANTS from "common/utils/data_validations"
import * as Yup from "yup"
import { useFormik } from "formik"
import { Form, Row, Col, Input, FormGroup, UncontrolledTooltip } from 'reactstrap';
import { getPrimaryButtonClass } from 'helpers/sf_utils';
import { MODE, MODIFICATION_TYPE, REQUIRED_FEATURE_FLAGS } from 'helpers/sf_constants';
import LabelWithTooltip from 'components/Common/LabelWithTooltip';
import { LEAD_INTEGRATION_STATUS } from 'helpers/sf_constants';
import { bsh_isAdminUserDetailsPresent } from 'helpers/sf_browser_storage_helper';
import useFeatureFlags from 'hooks/useFeatureFlags';

const API_FIELDS_TYPES_FOR_INTEGRATIONS = {
  FORM: "form",
  TEXT: "text",
  JSON: "json",
  XML: "xml",
}

const initial_failure_response_type = { name: "", failure_regex: "", modification_type: MODIFICATION_TYPE.new };


const ResponseType = ({ integration_id, mode, initialState, onSubmitState, openNextAccordion }) => {
  const [isFeatureFlagActive] = useFeatureFlags();

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      response_api_type: initialState?.lead_integration?.res_type || API_FIELDS_TYPES_FOR_INTEGRATIONS.JSON,
      response_success_message: initialState?.lead_integration?.res_success_regex || "",
      lead_integration_failure_reasons: initialState?.lead_integration?.lead_integration_failure_reasons || []
    },
    validationSchema: Yup.object({
      response_success_message: Yup.string().required(),
      lead_integration_failure_reasons: Yup.array().of(
        Yup.object().shape({
          name: Yup.string().when('modification_type', {
            is: (value) => value !== 'delete',
            then: Yup.string().required(CONSTANTS.STR_REQUIRED_FIELD),
            otherwise: Yup.string().notRequired(),
          }),
          failure_regex: Yup.string().when('modification_type', {
            is: (value) => value !== 'delete',
            then: Yup.string().required(CONSTANTS.STR_REQUIRED_FIELD),
            otherwise: Yup.string().notRequired(),
          }),
        })
      ),
    }),
    onSubmit: (values, { resetForm }) => {
      const lead_integration = {
        "res_type": values?.response_api_type,
        "res_success_regex": values?.response_success_message,
        "lead_integration_failure_reasons": isFeatureFlagActive(REQUIRED_FEATURE_FLAGS.SF_FEATURE_FLAG_SHOW_FAILURE_REASON_IN_INTEGRATIONS) ? (values?.lead_integration_failure_reasons || []).filter(reason => !(reason?.modification_type === MODIFICATION_TYPE.delete && !reason?.id)) : [],
        "status": initialState?.lead_integration?.status === LEAD_INTEGRATION_STATUS.DRAFT ? LEAD_INTEGRATION_STATUS.PAUSED : initialState?.lead_integration?.status,
      }
      if (mode === MODE.EDIT) {
        onSubmitState({ lead_integration })
      }
      if (mode === MODE.CREATE) {
        onSubmitState({ ...initialState, lead_integration: { ...initialState?.lead_integration, ...lead_integration } });
      }
      resetForm();
      openNextAccordion();
    },
  });

  const handleAddObject = () => {
    validation.setFieldValue('lead_integration_failure_reasons', [...validation?.values?.lead_integration_failure_reasons, { ...initial_failure_response_type }])
  };

  let indexFailureRow = 0;

  return (
    <React.Fragment>
      <Form
        onSubmit={e => {
          e.preventDefault()
          validation.handleSubmit()
          return false
        }}
      >
        <Row>
          <Col md={10} xl={10}>
            <div className="w-75 mb-3">
              <LabelWithTooltip
                name="Success"
                tooltip_message="Regular expression (Regex) or string to parse for successful response. If the response contains this string, we will treat this data integration as successful."
                target="response_success_message_tooltip"
                htmlFor="success"
              />
              <Input
                className="form-control"
                onChange={validation.handleChange}
                onBlur={validation.handleBlur}
                disabled={initialState?.lead_integration?.accept_reject && !bsh_isAdminUserDetailsPresent()}
                name="response_success_message"
                placeholder="Enter success message"
                value={validation.values.response_success_message}
                invalid={
                  validation.touched.response_success_message &&
                    validation.errors.response_success_message
                    ? true
                    : false
                }
              />
              {validation.touched.response_success_message &&
                validation.errors.response_success_message ? (
                <span className="text-danger">
                  {CONSTANTS.STR_REQUIRED_FIELD}
                </span>
              ) : null}
            </div>
          </Col>
          {
            isFeatureFlagActive(REQUIRED_FEATURE_FLAGS.SF_FEATURE_FLAG_SHOW_FAILURE_REASON_IN_INTEGRATIONS) && (
              <>
                <Col md={12} xl={12}>
                  <div className="w-75 my-3">
                    <LabelWithTooltip
                      name="Failure Reasons"
                      tooltip_message="Regular expression (Regex) or string to parse for failure response. If the response contains this string, we will treat this data integration as failure."
                      target="response_failure_message_tooltip"
                      htmlFor="failure"
                    />

                    {validation?.values?.lead_integration_failure_reasons?.length > 0 && validation?.values?.lead_integration_failure_reasons.map((object, index) => {
                      if (object?.modification_type === MODIFICATION_TYPE.delete) {
                        return;
                      }
                      return (
                        <Row className='mb-3' key={indexFailureRow++}>
                          <Col md={4}>
                            {!indexFailureRow && (<label>
                              Name
                            </label>)}
                            <Input
                              type="text"
                              className="form-control"
                              name={`lead_integration_failure_reasons[${index}].name`}
                              value={object.name}
                              onChange={(event) => {
                                validation.handleChange(event);
                                if (object?.id) {
                                  validation.handleChange({ target: { name: `lead_integration_failure_reasons[${index}].modification_type`, value: MODIFICATION_TYPE.update } });
                                }
                              }}
                              onBlur={validation.handleBlur}
                            />
                            {validation.touched.lead_integration_failure_reasons &&
                              validation.touched.lead_integration_failure_reasons[index] &&
                              validation.errors.lead_integration_failure_reasons &&
                              validation.errors.lead_integration_failure_reasons[index]?.name && (
                                <div className="error">{validation.errors.lead_integration_failure_reasons[index].name}</div>
                              )}
                          </Col>
                          <Col md={7}>
                            {!indexFailureRow && (<label>
                              Regex
                            </label>)}
                            <div className="d-flex">
                              <Input
                                type="text"
                                className="form-control"
                                name={`lead_integration_failure_reasons[${index}].failure_regex`}
                                value={object.failure_regex}
                                onChange={(event) => {
                                  validation.handleChange(event);
                                  if (object?.id) {
                                    validation.handleChange({ target: { name: `lead_integration_failure_reasons[${index}].modification_type`, value: MODIFICATION_TYPE.update } });
                                  }
                                }}
                                onBlur={validation.handleBlur}
                              />
                              <a onClick={(event) => validation.handleChange({ target: { name: `lead_integration_failure_reasons[${index}].modification_type`, value: MODIFICATION_TYPE.delete } })} id={`failure_row_close_btn_${indexFailureRow}`}>
                                <i className="mdi mdi-close h3 ms-5 pt-2"></i>
                                <UncontrolledTooltip placement="top" target={`failure_row_close_btn_${indexFailureRow}`}>
                                  Delete Failure Reason
                                </UncontrolledTooltip>
                              </a>
                            </div>
                            {validation.touched.lead_integration_failure_reasons &&
                              validation.touched.lead_integration_failure_reasons[index] &&
                              validation.errors.lead_integration_failure_reasons &&
                              validation.errors.lead_integration_failure_reasons[index]?.failure_regex && (
                                <div className="error">{validation.errors.lead_integration_failure_reasons[index].failure_regex}</div>
                              )}
                          </Col>
                        </Row>
                      )
                    })}
                  </div>
                </Col>
                <Col md={12}>
                  <FormGroup className='mt-3'>
                    <a onClick={handleAddObject} id={`filter_add_row`}>
                      <i className="bx bx-plus ms-1 text-sfblue align-middle"></i>
                      <label className="font-weight-semibold text-sfblue" style={{ cursor: "pointer" }}>
                        Add Failure Reason
                      </label>
                    </a>
                  </FormGroup>
                </Col>
              </>
            )
          }
          <Col md={12} xl={12}>
            <div className="mb-3 flex-container space-between">
              <button className={`btn ${getPrimaryButtonClass()} flex-item`} type="submit" name="submit"> {false ? "Saving" : (mode === MODE.EDIT ? "Save" : "Continue")}</button>
              <button hidden={false ? false : true} className="btn btn-light ms-3 flex-item" type="button" onClick={validation.resetForm} name="cancel">Cancel</button>
            </div>
          </Col>
        </Row>
      </Form>
    </React.Fragment>
  )
}

export default ResponseType